import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../../i18n";
import { useSettings } from "../../contexts/SettingsContext";
import apiClient from "../../lib/api";
import { toast } from "sonner";
import { Instagram, Twitter, Facebook, Youtube, Linkedin, Send } from "lucide-react";

// TikTok glyph (lucide-react has no TikTok icon)
const TikTok = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.21a8.16 8.16 0 0 0 4.77 1.52V6.31a4.85 4.85 0 0 1-1.84-.38v.76z"/>
  </svg>
);

const ICONS = { instagram: Instagram, twitter: Twitter, facebook: Facebook, youtube: Youtube, linkedin: Linkedin, tiktok: TikTok };

const isExternal = (href) => /^https?:\/\//i.test(href || "");

const FooterLink = ({ link, lang }) => {
  const label = lang === "nl" ? (link.label_nl || link.label_en) : (link.label_en || link.label_nl);
  if (isExternal(link.href)) {
    return <a href={link.href} target="_blank" rel="noopener noreferrer" className="hover:text-terracotta">{label}</a>;
  }
  return <Link to={link.href || "/"} className="hover:text-terracotta">{label}</Link>;
};

const Footer = () => {
  const { t, lang } = useI18n();
  const { settings } = useSettings();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await apiClient.post("/newsletter", { email, language: lang });
      if (data?.discount_code) {
        toast.success(lang === "nl" ? `Bedankt! Jouw 10% code: ${data.discount_code}` : `Thanks! Your 10% code: ${data.discount_code}`, { duration: 8000 });
      } else {
        toast.success(lang === "nl" ? "Je bent al ingeschreven 🎉" : "You're already subscribed 🎉");
      }
      setEmail("");
    } catch {
      toast.error(lang === "nl" ? "Er ging iets mis" : "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const f = settings?.footer || {};
  const ribbon = (lang === "nl" ? f.ribbon_nl : f.ribbon_en) || (lang === "nl"
    ? "🇳🇱 GRATIS VERZENDING VANAF €50 · POSTNL & DHL · 30 DAGEN RETOUR · iDEAL · KLARNA"
    : "🇳🇱 FREE SHIPPING FROM €50 · POSTNL & DHL · 30-DAY RETURNS · iDEAL · KLARNA");
  const newsletterBlurb = (lang === "nl" ? f.newsletter_blurb_nl : f.newsletter_blurb_en) || t("newsletter_sub");
  const columns = f.columns || [];
  const socialCfg = settings?.social || {};
  const socialFromSettings = [
    { key: "instagram", label: "Instagram", url: socialCfg.instagram_url },
    { key: "facebook", label: "Facebook", url: socialCfg.facebook_url },
    { key: "tiktok", label: "TikTok", url: socialCfg.tiktok_url },
    { key: "linkedin", label: "LinkedIn", url: socialCfg.linkedin_url },
    { key: "twitter", label: "Twitter", url: socialCfg.twitter_url },
    { key: "youtube", label: "YouTube", url: socialCfg.youtube_url },
  ].filter((s) => !!s.url).map((s) => ({ label: s.label, href: s.url, icon: s.key }));
  const legacySocial = (f.social || []).filter((s) => !!s?.href && !socialFromSettings.some((x) => x.icon === (s.icon || "").toLowerCase()));
  const social = [...socialFromSettings, ...legacySocial];
  const badges = f.payment_badges || ["iDEAL", "Klarna", "PostNL", "DHL"];
  const legalTemplate = (lang === "nl" ? f.legal_line_nl : f.legal_line_en) || "© {year} SnapShop — KvK {kvk} — BTW {btw}";
  const legalLine = legalTemplate
    .replace("{year}", String(new Date().getFullYear()))
    .replace("{kvk}", settings?.kvk_number || "00000000")
    .replace("{btw}", settings?.btw_number || "NL000000000B01");

  return (
    <footer className="bg-ink text-white mt-20" data-testid="site-footer">
      <div className="border-b border-white/10 bg-terracotta">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-3 text-center text-xs md:text-sm font-label text-white" data-testid="footer-ribbon">
          {ribbon}
        </div>
      </div>
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 grid grid-cols-2 md:grid-cols-4 gap-10">
        <div className="col-span-2 md:col-span-1">
          <div className="font-display text-2xl">
            <span className="font-light">Snap</span><span className="text-terracotta">Shop</span>
          </div>
          <p className="text-white/60 text-sm mt-3 leading-relaxed">{newsletterBlurb}</p>
          <form onSubmit={subscribe} className="mt-5 flex items-stretch border border-white/20">
            <input data-testid="newsletter-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("email")} className="flex-1 bg-transparent text-sm px-3 py-3 outline-none placeholder-white/40" />
            <button data-testid="newsletter-submit" disabled={loading} className="px-4 bg-terracotta hover:bg-[#b85f47] transition-colors text-sm font-medium"><Send className="w-4 h-4" /></button>
          </form>
          <div className="text-[10px] text-terracotta font-label mt-2">{lang === "nl" ? "10% korting bij inschrijving" : "10% off when you subscribe"}</div>
        </div>
        {columns.map((col, ci) => (
          <div key={ci} data-testid={`footer-column-${ci}`}>
            <div className="font-label text-white/60 mb-4">{lang === "nl" ? (col.title_nl || col.title_en) : (col.title_en || col.title_nl)}</div>
            <ul className="space-y-2 text-sm">
              {(col.links || []).map((link, li) => (
                <li key={li}><FooterLink link={link} lang={lang} /></li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-8 flex flex-col md:flex-row justify-between items-center gap-6 text-xs text-white/50">
          <div data-testid="footer-legal">{legalLine}</div>
          <div className="flex items-center gap-5">
            {badges.map((b, i) => <span key={i} className="font-label tracking-[0.2em]">{b}</span>)}
          </div>
          <div className="flex items-center gap-3">
            {social.map((s, i) => {
              const Icon = ICONS[(s.icon || "").toLowerCase()] || Instagram;
              return (
                <a key={i} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label} className="hover:text-white" data-testid={`footer-social-${i}`}>
                  <Icon className="w-4 h-4" />
                </a>
              );
            })}
          </div>
        </div>
      </div>

      <div className="overflow-hidden select-none pointer-events-none">
        <div className="font-display tracking-tighter text-white/5 text-[18vw] leading-[0.8] -mb-6 -mx-6 font-light">SNAPSHOP</div>
      </div>
    </footer>
  );
};

export default Footer;
